/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { defineMessage, FormattedMessage } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import EyeOpenIcon from '@atlaskit/icon/core/eye-open';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import SectionMessage from '@atlaskit/section-message';
import { token } from '@atlaskit/tokens';

import type { SurveyBuilderProps, SurveyQuestion } from '../../types';

import { CheckboxGroupInput } from './components/checkbox-group-input';
import { MultiSelectInput } from './components/multi-select-input';
import { RadioGroupInput } from './components/radio-group-input';
import { RatingInput } from './components/rating-input';
import { ScaleInput } from './components/scale-input';
import { SelectInput } from './components/select-input';
import { TextInput } from './components/text-input';
import { TextareaInput } from './components/textarea-input';

const styles = cssMap({
	container: {
		paddingTop: token('space.300'),
		paddingRight: token('space.300'),
		paddingBottom: token('space.300'),
		paddingLeft: token('space.300'),
		gap: token('space.300'),
		maxWidth: '600px',
		margin: '0 auto',
	},
	previewBanner: {
		borderRadius: token('radius.small'),
		paddingTop: token('space.150'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
	},
});

const previewMessage = defineMessage({
	id: 'survey-builder.preview-message.non-final',
	defaultMessage: "This is only a preview, you can't edit questions inline.",
	description: 'Message displayed when the survey is in preview mode',
});

const previewMessageRedesign = defineMessage({
	id: 'survey-builder.preview-message-redesign.non-final',
	defaultMessage: 'This is a preview. Create the survey or ask Rovo to make changes.',
	description: 'Message displayed when the survey is in preview mode',
});

const unsupportedMessage = defineMessage({
	id: 'survey-builder.unsupported-message.non-final',
	defaultMessage: 'This is an unsupported question type.',
	description: 'Message displayed when the survey type returned is unsupported',
});

const renderQuestionField = ({
	type,
	text,
	isRequired,
	choices = [],
}: SurveyQuestion): JSX.Element => {
	switch (type) {
		case 'tl':
		case 'ts':
			return <TextInput key={text} label={text} name={text} id={text} isRequired={isRequired} />;
		case 'pg':
			return (
				<TextareaInput key={text} label={text} name={text} id={text} isRequired={isRequired} />
			);
		case 'cs':
			return (
				<RadioGroupInput
					key={text}
					label={text}
					name={text}
					isRequired={isRequired}
					options={choices.map((choice) => ({ label: choice, value: choice }))}
				/>
			);
		case 'cm':
			return (
				<CheckboxGroupInput
					key={text}
					label={text}
					name={text}
					isRequired={isRequired}
					options={choices.map((choice) => ({ label: choice, value: choice }))}
				/>
			);
		case 'cl':
			return (
				<MultiSelectInput
					key={text}
					label={text}
					name={text}
					id={text}
					isRequired={isRequired}
					options={choices.map((choice) => ({ label: choice, value: choice }))}
				/>
			);
		case 'cd':
			return (
				<SelectInput
					key={text}
					label={text}
					name={text}
					id={text}
					isRequired={isRequired}
					options={choices.map((choice) => ({ label: choice, value: choice }))}
				/>
			);
		case 'da':
		case 'dt':
		case 'no':
		case 'ti':
		case 'tu':
		case 'te':
			return <TextInput key={text} label={text} name={text} id={text} isRequired={isRequired} />;
		case 'rg':
			return (
				<RatingInput
					key={text}
					label={text}
					name={text}
					id={text}
					isRequired={isRequired}
					options={choices}
				/>
			);
		case 'sc':
			return (
				<ScaleInput
					key={text}
					label={text}
					name={text}
					id={text}
					isRequired={isRequired}
					options={choices}
				/>
			);
		default:
			return (
				<Text key={text}>
					<FormattedMessage {...unsupportedMessage} />
				</Text>
			);
	}
};

export const SurveyBuilder = ({
	title,
	description,
	questions,
	isPreview,
	testId,
}: SurveyBuilderProps): JSX.Element => (
	<Stack xcss={styles.container} testId={testId}>
		{isPreview &&
			(fg('phx_survey_builder_preview_banner_redesign') ? (
				<Box backgroundColor="color.background.neutral" xcss={styles.previewBanner}>
					<Inline space="space.100" alignBlock="center">
						<EyeOpenIcon label="" color="currentColor" />
						<Text>
							<FormattedMessage {...previewMessageRedesign} />
						</Text>
					</Inline>
				</Box>
			) : (
				<SectionMessage appearance="information">
					<FormattedMessage {...previewMessage} />
				</SectionMessage>
			))}
		<Stack space="space.100">
			<Heading size="medium">{title}</Heading>
			<Text>{description}</Text>
		</Stack>
		<Stack space="space.250">
			{questions.map(({ type, ...rest }) => renderQuestionField({ type, ...rest }))}
		</Stack>
	</Stack>
);
